import { toast } from 'sonner';
import { handleApiError, normalizeUnexpectedResponseMessage } from '@/lib/utils';
import { getStoredAuthToken } from '@/lib/authStorage';
import { notifyAiCreditsRequired } from '@/lib/creditsGuard';
import { getAttributionHeaders } from '@/lib/utm';
import type { CreditGateUiType } from '@/lib/creditsTracking';

export interface SSEStreamResult<T = Record<string, unknown>> {
  content: string;
  finalData: Partial<T> | null;
  truncated?: boolean;
}

export interface SSEStreamCallbacks {
  onMessage?: (chunk: string) => void;
  onHeartbeat?: (payload?: string) => void;
  onRetrying?: (payload?: string) => void;
  suppressErrorToast?: boolean;
  showCreditsModal?: boolean;
  creditsModalTitle?: string;
  creditsModalCopy?: string;
  creditsModalProductArea?: string;
  creditsModalSurface?: string;
  creditsModalStage?: string;
  creditsModalTrigger?: string;
  creditsModalUiType?: CreditGateUiType;
  creditsModalSource?: string;
  creditsModalOperation?: string;
  creditsModalTaskCode?: string | null;
  creditsModalMetadata?: Record<string, unknown>;
  contentEventNames?: string[];
  acceptImplicitContent?: boolean;
}

export async function fetchSSEStream<T = Record<string, unknown>>(
  url: string,
  body: unknown,
  callbacksOrOnMessage?: SSEStreamCallbacks | ((chunk: string) => void),
  extraHeaders?: Record<string, string>,
  signal?: AbortSignal,
): Promise<SSEStreamResult<T>> {
  const callbacks: SSEStreamCallbacks =
    typeof callbacksOrOnMessage === 'function'
      ? { onMessage: callbacksOrOnMessage }
      : callbacksOrOnMessage || {};
  const shouldShowErrorToast = !callbacks.suppressErrorToast;
  const showCreditsRequiredModal = (trigger: string) => {
    if (!callbacks.showCreditsModal) return;
    notifyAiCreditsRequired({
      title: callbacks.creditsModalTitle,
      copy: callbacks.creditsModalCopy,
      productArea: callbacks.creditsModalProductArea,
      surface: callbacks.creditsModalSurface,
      stage: callbacks.creditsModalStage,
      trigger: callbacks.creditsModalTrigger || trigger || 'sse_stream_error',
      uiType: callbacks.creditsModalUiType || 'modal',
      source: callbacks.creditsModalSource || 'sse_stream',
      operation: callbacks.creditsModalOperation,
      taskCode: callbacks.creditsModalTaskCode,
      metadata: {
        ...(callbacks.creditsModalMetadata || {}),
        ...(trigger ? { error_trigger: trigger } : {}),
      },
    });
  };
  const contentEventNames = new Set(
    (callbacks.contentEventNames?.length ? callbacks.contentEventNames : ['delta']).map((event) =>
      event.trim().toLowerCase(),
    ),
  );
  const acceptImplicitContent = callbacks.acceptImplicitContent ?? true;

  const baseUrl = import.meta.env.DEV ? '' : import.meta.env.VITE_API_BASE_URL || '';
  const finalUrl = url.startsWith('http') ? url : `${baseUrl}${url}`;

  const token = getStoredAuthToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...getAttributionHeaders(),
    ...extraHeaders,
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
    headers['token'] = token;
  }

  const response = await fetch(finalUrl, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
    signal,
  });

  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    const data = await response.json();
    const { code, errorCode, success, value: errorMessage, message, config = {} } = data;
    const codeStatus = code ? code : errorCode;
    const errValue = message ? message : errorMessage;
    if (!success || (codeStatus && codeStatus !== '0')) {
      const normalizedMessage = handleApiError(codeStatus, errValue, config?._skipAuthCheck);
      console.error(`[Fetch] Error [${codeStatus}]: ${normalizedMessage}`);
      if (String(codeStatus) === 'INSUFFICIENT_POINTS') {
        showCreditsRequiredModal('json_error_response');
      } else if (shouldShowErrorToast) {
        toast.error(`${normalizedMessage}`);
      }
      const err = new Error(normalizedMessage);
      (err as Error & { code?: string }).code = codeStatus;
      throw err;
    }
    return { content: '', finalData: data };
  }

  if (!response.ok) {
    const errorText = await response.text();
    const err = new Error(normalizeUnexpectedResponseMessage(errorText));
    (err as Error & { code?: string }).code = String(response.status);
    throw err;
  }

  const reader = response.body?.getReader();
  if (!reader) throw new Error('Failed to initialize stream reader.');

  const decoder = new TextDecoder();
  let fullContent = '';
  let finalData: Partial<T> | null = null;
  let isFirstChunk = true;
  let lineBuffer = '';
  let currentEvent = '';
  let streamError: Error | null = null;
  let isTruncated = false;
  let shouldTerminate = false;

  const isHeartbeatEvent = (eventName: string) => {
    const normalized = eventName.trim().toLowerCase();
    return normalized === 'heartbeat' || normalized === 'ping' || normalized === 'keepalive';
  };

  const appendContent = (content: unknown) => {
    const text = typeof content === 'string' ? content : String(content ?? '');
    if (!text) return;
    fullContent += text;
    callbacks.onMessage?.(text);
  };

  const handleStructuredError = (message: string, code?: string) => {
    if (shouldShowErrorToast && code !== 'INSUFFICIENT_POINTS') {
      toast.error(message);
    }
    const err = new Error(message);
    (err as Error & { code?: string }).code = code;
    streamError = err;
    shouldTerminate = true;
  };

  function processLine(line: string) {
    const trimmed = line.trim();
    if (!trimmed) return;

    if (trimmed.startsWith(':')) {
      callbacks.onHeartbeat?.(trimmed.slice(1).trim());
      return;
    }

    if (trimmed.startsWith('event:')) {
      currentEvent = trimmed.replace('event:', '').trim();
      return;
    }

    if (isFirstChunk) {
      isFirstChunk = false;
      if (!trimmed.startsWith('data:')) {
        try {
          const parsed = JSON.parse(trimmed);
          if (parsed.errorCode && parsed.errorCode !== '0') {
            if (parsed.errorCode === 'INSUFFICIENT_POINTS') {
              showCreditsRequiredModal('stream_initial_error');
            }
            handleStructuredError(
              parsed.value || parsed.message || `Request failed (${parsed.errorCode})`,
              parsed.errorCode,
            );
            return;
          }
        } catch (error) {
          if (!(error instanceof SyntaxError)) {
            streamError = error as Error;
            shouldTerminate = true;
            return;
          }
        }
      }
    }

    if (!trimmed.startsWith('data:')) {
      return;
    }

    const data = trimmed.replace('data:', '').trim();
    const normalizedEvent = currentEvent.trim().toLowerCase();

    if (isHeartbeatEvent(normalizedEvent)) {
      callbacks.onHeartbeat?.(data);
      return;
    }

    try {
      const parsed = JSON.parse(data);
      if (parsed.code === 'GENERATE_FAILED' || (parsed.errorCode && parsed.errorCode !== '0')) {
        const errMsg = normalizeUnexpectedResponseMessage(
          parsed.message ||
            parsed.value ||
            `Generation failed (${parsed.code || parsed.errorCode})`,
        );
        if (parsed.code === 'INSUFFICIENT_POINTS' || parsed.errorCode === 'INSUFFICIENT_POINTS') {
          showCreditsRequiredModal('stream_structured_error');
        }
        handleStructuredError(errMsg, parsed.code || parsed.errorCode);
        return;
      }
    } catch (error) {
      if (!(error instanceof SyntaxError)) {
        streamError = error as Error;
        shouldTerminate = true;
        return;
      }
    }

    if (normalizedEvent === 'error') {
      try {
        const parsed = JSON.parse(data);
        const errCode = parsed.code as string | undefined;
        const errMsg = normalizeUnexpectedResponseMessage(
          parsed.message || parsed.value || `Request failed (${parsed.code || parsed.errorCode})`,
        );
        if (errCode === 'INSUFFICIENT_POINTS' || parsed.errorCode === 'INSUFFICIENT_POINTS') {
          showCreditsRequiredModal('stream_error_event');
        }
        handleStructuredError(errMsg, errCode || parsed.code || parsed.errorCode);
      } catch {
        const normalizedMessage = normalizeUnexpectedResponseMessage(data);
        handleStructuredError(normalizedMessage);
      }
      return;
    }

    if (normalizedEvent === 'generation_retrying') {
      fullContent = '';
      callbacks.onRetrying?.(data);
      return;
    }

    if (contentEventNames.has(normalizedEvent)) {
      try {
        const delta = JSON.parse(data);
        appendContent(
          delta.data?.content ?? delta.data?.delta ?? delta.content ?? delta.delta ?? '',
        );
      } catch {
        appendContent(data);
      }
      return;
    }

    if (normalizedEvent === 'done') {
      try {
        finalData = JSON.parse(data);
        shouldTerminate = true;
      } catch (error) {
        console.warn('[SSE] Failed to parse done payload:', error);
      }
      return;
    }

    if (normalizedEvent === 'truncated') {
      isTruncated = true;
      shouldTerminate = true;
      return;
    }

    if (!acceptImplicitContent) {
      return;
    }

    try {
      const parsed = JSON.parse(data);
      const content = parsed.data?.content ?? parsed.data?.delta ?? parsed.content ?? parsed.delta;
      if (content) {
        appendContent(content);
      }
      if (parsed.data?.isDone || parsed.isDone) {
        finalData = parsed.data || parsed;
        shouldTerminate = true;
      }
    } catch {
      appendContent(data);
    }
  }

  while (true) {
    const { done, value } = await reader.read();
    if (done) {
      if (lineBuffer) {
        processLine(lineBuffer);
      }
      break;
    }

    const chunkText = decoder.decode(value, { stream: true });
    const combinedText = lineBuffer + chunkText;
    const lines = combinedText.split('\n');
    lineBuffer = lines.pop() || '';

    for (const line of lines) {
      processLine(line);
      if (shouldTerminate) {
        break;
      }
    }

    if (streamError) throw streamError;
    if (shouldTerminate || isTruncated || finalData) {
      try {
        await reader.cancel();
      } catch {
        // Ignore reader cancellation failures after terminal SSE events.
      }
      break;
    }
  }

  if (streamError) throw streamError;

  return { content: fullContent, finalData, truncated: isTruncated };
}
